import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Menu } from 'primereact/menu';
import { Badge } from 'primereact/badge';
import { useNavigate } from 'react-router-dom';
import { API_URLS } from '../../Services/servicioURL';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Button } from 'primereact/button';
import GlobalSearch from '../GobalSearch/GlobalSearch';
import { useGlobalSearch } from '../hooks/useGlobalSearch';

interface Notificacion {
    id: number;
    folio: number;
    titulo: string;
    empleado: string;
    fecha: string;
    estatus: string;
    leida: boolean;
}

interface PeticionAPI {
    folio: number;
    cveemp: string;
    fecha_hora: string;
    titulo: string;
    justificacion: string;
    sucursal: string;
    departamento: string;
    estatus: string;
    empleado?: {
        cveemp: string;
        nomemp: string;
    };
}

// NUEVO: Claves para localStorage
const STORAGE_KEYS = {
    PETICIONES_NOTIFICADAS: 'peticiones_notificadas',
    NOTIFICACIONES_PERSISTENTES: 'notificaciones_persistentes',
    ULTIMA_VERIFICACION: 'ultima_verificacion_peticiones',
    DARK_MODE: 'dark_mode_enabled' // NUEVO: Para persistir modo oscuro
};

const Navbar: React.FC = () => {
    const menu = useRef<Menu>(null);
    const notificationsPanel = useRef<OverlayPanel>(null);
    // NUEVO: Ref para el menú de navegación rápida
    const quickNavMenu = useRef<Menu>(null);
    const navigate = useNavigate();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    
    // NUEVO: Hook para búsqueda global
    const { isSearchVisible, searchQuery, showSearch, hideSearch } = useGlobalSearch();
    
    // Estados para notificaciones
    const [notificaciones, setNotificaciones] = useState<Notificacion[]>([]);
    const [ultimaVerificacion, setUltimaVerificacion] = useState<Date>(new Date());
    const [sonidoHabilitado, setSonidoHabilitado] = useState<boolean>(true);
    // MODIFICADO: Estado para trackear peticiones que ya fueron notificadas con sonido
    const [peticionesNotificadas, setPeticionesNotificadas] = useState<Set<number>>(new Set());
    
    // NUEVO: Estado para controlar si el menú de navegación está expandido
    const [menuNavegacionExpandido, setMenuNavegacionExpandido] = useState<boolean>(true);
    
    // NUEVO: Estado para modo oscuro
    const [modoOscuro, setModoOscuro] = useState<boolean>(false);

    // NUEVO: Estados para el botón scroll to top
    const [mostrarScrollToTop, setMostrarScrollToTop] = useState<boolean>(false);

    // NUEVO: Función para manejar el scroll y mostrar/ocultar el botón
    const handleScroll = useCallback(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        // Mostrar el botón cuando se haya bajado más de 300px
        setMostrarScrollToTop(scrollTop > 300);
    }, []);

    // NUEVO: Función para hacer scroll suave hacia arriba
    const scrollToTop = useCallback(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, []);

    // NUEVO: Efecto para agregar/remover el listener de scroll
    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        
        // Cleanup
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    // NUEVO: Función para aplicar modo oscuro globalmente - SIMPLIFICADO Y FUNCIONAL
    const aplicarModoOscuro = useCallback((activar: boolean) => {
        const body = document.body;
        const html = document.documentElement;
        
        if (activar) {
            body.classList.add('dark-mode');
            html.classList.add('dark-mode');
            body.style.backgroundColor = '#1a1a1a';
            body.style.color = '#ffffff';
        } else {
            body.classList.remove('dark-mode');
            html.classList.remove('dark-mode');
            body.style.backgroundColor = '';
            body.style.color = '';
        }
        
        // Guardar preferencia
        localStorage.setItem(STORAGE_KEYS.DARK_MODE, JSON.stringify(activar));
    }, []);

    // NUEVO: Función para toggle del modo oscuro
    const toggleModoOscuro = useCallback(() => {
        const nuevoModo = !modoOscuro;
        setModoOscuro(nuevoModo);
        aplicarModoOscuro(nuevoModo);
    }, [modoOscuro, aplicarModoOscuro]);

    // NUEVO: Función para verificar si una petición está pendiente
    const esPeticionPendiente = useCallback((estatus: string): boolean => {
        const estatusNormalizado = estatus?.toUpperCase().replace(/\s+/g, '_') || '';
        return estatusNormalizado === 'PENDIENTE';
    }, []);

    // NUEVO: Función para cargar datos desde localStorage
    const cargarDatosDesdeStorage = useCallback(() => {
        try {
            // Cargar peticiones notificadas (que ya sonaron)
            const notificadasStorage = localStorage.getItem(STORAGE_KEYS.PETICIONES_NOTIFICADAS);
            if (notificadasStorage) {
                const notificadasArray = JSON.parse(notificadasStorage);
                setPeticionesNotificadas(new Set(notificadasArray));
            }

            // Cargar notificaciones persistentes
            const notificacionesStorage = localStorage.getItem(STORAGE_KEYS.NOTIFICACIONES_PERSISTENTES);
            if (notificacionesStorage) {
                const notificacionesArray = JSON.parse(notificacionesStorage);
                setNotificaciones(notificacionesArray);
            }

            // Cargar última verificación
            const ultimaVerificacionStorage = localStorage.getItem(STORAGE_KEYS.ULTIMA_VERIFICACION);
            if (ultimaVerificacionStorage) {
                setUltimaVerificacion(new Date(ultimaVerificacionStorage));
            }

            // NUEVO: Cargar estado del menú de navegación
            const menuExpandidoStorage = localStorage.getItem('navbar_menu_expandido');
            if (menuExpandidoStorage !== null) {
                setMenuNavegacionExpandido(JSON.parse(menuExpandidoStorage));
            }

            // NUEVO: Cargar preferencia de modo oscuro
            const modoOscuroStorage = localStorage.getItem(STORAGE_KEYS.DARK_MODE);
            if (modoOscuroStorage !== null) {
                const modoOscuroActivado = JSON.parse(modoOscuroStorage);
                setModoOscuro(modoOscuroActivado);
                aplicarModoOscuro(modoOscuroActivado);
            }
        } catch (error) {
        }
    }, [aplicarModoOscuro]);

    // NUEVO: Función para guardar datos en localStorage
    const guardarDatosEnStorage = useCallback((
        notificadas: Set<number>, 
        notificacionesActuales: Notificacion[],
        ultimaVerif: Date
    ) => {
        try {
            localStorage.setItem(STORAGE_KEYS.PETICIONES_NOTIFICADAS, JSON.stringify(Array.from(notificadas)));
            localStorage.setItem(STORAGE_KEYS.NOTIFICACIONES_PERSISTENTES, JSON.stringify(notificacionesActuales));
            localStorage.setItem(STORAGE_KEYS.ULTIMA_VERIFICACION, ultimaVerif.toISOString());
        } catch (error) {
        }
    }, []);

    // NUEVO: Función para alternar el estado del menú de navegación
    const toggleMenuNavegacion = useCallback(() => {
        const nuevoEstado = !menuNavegacionExpandido;
        setMenuNavegacionExpandido(nuevoEstado);
        localStorage.setItem('navbar_menu_expandido', JSON.stringify(nuevoEstado));
    }, [menuNavegacionExpandido]);

    // NUEVO: Efecto para cargar datos al montar el componente
    useEffect(() => {
        cargarDatosDesdeStorage();
    }, [cargarDatosDesdeStorage]);

    // NUEVO: Efecto para manejar el responsive automático
    useEffect(() => {
        const handleResize = () => {
            // Contraer automáticamente en pantallas pequeñas
            if (window.innerWidth < 1200) {
                setMenuNavegacionExpandido(false);
            } else {
                // Restaurar estado guardado en pantallas grandes
                const menuExpandidoStorage = localStorage.getItem('navbar_menu_expandido');
                if (menuExpandidoStorage !== null) {
                    setMenuNavegacionExpandido(JSON.parse(menuExpandidoStorage));
                } else {
                    setMenuNavegacionExpandido(true);
                }
            }
        };

        // Ejecutar al cargar
        handleResize();

        // Agregar listener
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Función para reproducir sonido de notificación
    const reproducirSonidoNotificacion = useCallback(() => {
        if (!sonidoHabilitado) return;
        
        try {
            // Reutilizar o crear contexto de audio
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
            }
            
            const audioContext = audioContextRef.current;
            
            // Verificar si el contexto está suspendido (política de navegadores)
            if (audioContext.state === 'suspended') {
                audioContext.resume();
            }
            
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
            oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
            oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);
            
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
            
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.3);
        } catch (error) {
        }
    }, [sonidoHabilitado]);

    // Función para obtener peticiones desde la API
const obtenerPeticiones = useCallback(async (): Promise<PeticionAPI[]> => {
    // NUEVO: Verificación simple de conectividad
    if (!navigator.onLine) {
        return []; // Si no hay conexión, retornar array vacío sin intentar fetch
    }

    return new Promise((resolve) => {
        fetch(API_URLS.peticiones.obtener, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            cache: 'no-cache'
        })
        .then(response => {
            if (response.ok) {
                return response.json();
            }
            return [];
        })
        .then(data => resolve(data || []))
        .catch(() => resolve([])); // Siempre resolver con array vacío en caso de error
    });
}, []);

    // MODIFICADO: Función para verificar y actualizar notificaciones
    const verificarNuevasPeticiones = useCallback(async () => {
        try {
            const peticiones = await obtenerPeticiones();
            const ahora = new Date();
            
            // NUEVO: Filtrar SOLO peticiones con estatus PENDIENTE
            const peticionesPendientes = peticiones.filter(peticion => 
                esPeticionPendiente(peticion.estatus)
            );

            // MODIFICADO: Crear mapa de peticiones pendientes actuales
            const peticionesPendientesMap = new Map(
                peticionesPendientes.map(p => [p.folio, p])
            );

            // NUEVO: Actualizar notificaciones existentes y agregar nuevas
            const notificacionesActualizadas: Notificacion[] = [];
            const foliosExistentes = new Set(notificaciones.map(n => n.folio));
            
            // Mantener notificaciones existentes que siguen siendo pendientes
            notificaciones.forEach(notificacion => {
                if (peticionesPendientesMap.has(notificacion.folio)) {
                    // La petición sigue pendiente, mantener la notificación
                    const peticionActual = peticionesPendientesMap.get(notificacion.folio)!;
                    notificacionesActualizadas.push({
                        ...notificacion,
                        // Actualizar datos que pueden haber cambiado
                        titulo: peticionActual.titulo,
                        empleado: peticionActual.empleado?.nomemp || peticionActual.cveemp,
                        estatus: peticionActual.estatus
                    });
                }
                // Si la petición ya no está pendiente, no la agregamos (se elimina automáticamente)
            });

            // Agregar nuevas peticiones pendientes
            const peticionesParaSonido: PeticionAPI[] = [];
            peticionesPendientes.forEach(peticion => {
                if (!foliosExistentes.has(peticion.folio)) {
                    // Nueva petición pendiente
                    const nuevaNotificacion: Notificacion = {
                        id: Date.now() + Math.random(),
                        folio: peticion.folio,
                        titulo: peticion.titulo,
                        empleado: peticion.empleado?.nomemp || peticion.cveemp,
                        fecha: peticion.fecha_hora,
                        estatus: peticion.estatus,
                        leida: false
                    };
                    notificacionesActualizadas.push(nuevaNotificacion);

                    // Verificar si debe sonar
                    if (!peticionesNotificadas.has(peticion.folio)) {
                        peticionesParaSonido.push(peticion);
                    }
                }
            });

            // Ordenar por fecha (más recientes primero) y limitar a 10
            notificacionesActualizadas.sort((a, b) => 
                new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
            );
            const notificacionesFinales = notificacionesActualizadas.slice(0, 10);

            // Actualizar estado
            setNotificaciones(notificacionesFinales);

            // MODIFICADO: Solo reproducir sonido para peticiones realmente nuevas
            if (peticionesParaSonido.length > 0) {
                reproducirSonidoNotificacion();
                
                // Marcar estas peticiones como notificadas con sonido
                const nuevasPeticionesNotificadas = new Set(peticionesNotificadas);
                peticionesParaSonido.forEach(peticion => {
                    nuevasPeticionesNotificadas.add(peticion.folio);
                });
                setPeticionesNotificadas(nuevasPeticionesNotificadas);

                // Guardar en localStorage
                guardarDatosEnStorage(nuevasPeticionesNotificadas, notificacionesFinales, ahora);
            } else {
                // Guardar sin cambios en peticiones notificadas
                guardarDatosEnStorage(peticionesNotificadas, notificacionesFinales, ahora);
            }

            setUltimaVerificacion(ahora);
        } catch (error) {
        }
    }, [obtenerPeticiones, reproducirSonidoNotificacion, peticionesNotificadas, notificaciones, guardarDatosEnStorage, esPeticionPendiente]);

    // Efecto para verificar peticiones periódicamente cada 30 segundos
    useEffect(() => {
        // Verificación inicial con delay para asegurar que los datos se cargaron
        const timeoutId = setTimeout(() => {
            verificarNuevasPeticiones();
        }, 1000);

        // Configurar polling cada 30 segundos
        intervalRef.current = setInterval(() => {
            verificarNuevasPeticiones();
        }, 30000); // 30 segundos

        // Cleanup function
        return () => {
            clearTimeout(timeoutId);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [verificarNuevasPeticiones]);

    // Efecto para limpiar el contexto de audio al desmontar
    useEffect(() => {
        return () => {
            if (audioContextRef.current) {
                audioContextRef.current.close();
                audioContextRef.current = null;
            }
        };
    }, []);

    // Función para formatear fecha
    const formatearFecha = useCallback((fechaString: string): string => {
        try {
            const fecha = new Date(fechaString);
            const ahora = new Date();
            const diferencia = ahora.getTime() - fecha.getTime();
            const minutos = Math.floor(diferencia / (1000 * 60));
            const horas = Math.floor(diferencia / (1000 * 60 * 60));
            const dias = Math.floor(diferencia / (1000 * 60 * 60 * 24));

            if (minutos < 1) return 'Hace un momento';
            if (minutos < 60) return `Hace ${minutos} min`;
            if (horas < 24) return `Hace ${horas} h`;
            if (dias < 7) return `Hace ${dias} d`;
            
            return fecha.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return 'Fecha inválida';
        }
    }, []);

    // Función para obtener color del estatus
    const getEstatusColor = useCallback((estatus: string): string => {
        const estatusNormalizado = estatus?.toUpperCase().replace(/\s+/g, '_') || 'PENDIENTE';
        const colores = {
            'PENDIENTE': '#ffc107',
            'EN_PROCESO': '#17a2b8',
            'APROBADA': '#28a745',
            'RECHAZADA': '#dc3545',
            'COMPLETADA': '#28a745',
            'CANCELADA': '#6c757d'
        };
        return colores[estatusNormalizado as keyof typeof colores] || '#ffc107';
    }, []);

    // Función para marcar notificación como leída y navegar
    const handleNotificacionClick = useCallback((notificacion: Notificacion) => {
        // Marcar como leída
        setNotificaciones(prev => {
            const updated = prev.map(n => 
                n.id === notificacion.id ? { ...n, leida: true } : n
            );
            // Guardar en localStorage
            guardarDatosEnStorage(peticionesNotificadas, updated, ultimaVerificacion);
            return updated;
        });
        
        // Navegar a peticionesadmin con filtro específico
        navigate(`/peticionesadmin?folio=${notificacion.folio}`);
        
        // Cerrar panel
        notificationsPanel.current?.hide();
    }, [navigate, peticionesNotificadas, ultimaVerificacion, guardarDatosEnStorage]);

    // MODIFICADO: Función para marcar todas como leídas
    const marcarTodasComoLeidas = useCallback(() => {
        setNotificaciones(prev => {
            const updated = prev.map(n => ({ ...n, leida: true }));
            
            // Marcar todas las notificaciones actuales como notificadas (para evitar sonidos futuros)
            const nuevasPeticionesNotificadas = new Set(peticionesNotificadas);
            updated.forEach(notif => {
                nuevasPeticionesNotificadas.add(notif.folio);
            });
            setPeticionesNotificadas(nuevasPeticionesNotificadas);

            // Guardar en localStorage
            guardarDatosEnStorage(nuevasPeticionesNotificadas, updated, ultimaVerificacion);
            return updated;
        });
    }, [peticionesNotificadas, ultimaVerificacion, guardarDatosEnStorage]);

    // Función para ir a todas las peticiones
    const irAPeticiones = useCallback(() => {
        navigate('/peticionesadmin');
        notificationsPanel.current?.hide();
    }, [navigate]);

    // NUEVO: Función para limpiar historial de notificaciones (útil para desarrollo/testing)
    const limpiarHistorialNotificaciones = useCallback(() => {
        localStorage.removeItem(STORAGE_KEYS.PETICIONES_NOTIFICADAS);
        localStorage.removeItem(STORAGE_KEYS.NOTIFICACIONES_PERSISTENTES);
        localStorage.removeItem(STORAGE_KEYS.ULTIMA_VERIFICACION);
        setPeticionesNotificadas(new Set());
        setNotificaciones([]);
    }, []);

    // Contar notificaciones no leídas
    const notificacionesNoLeidas = notificaciones.filter(n => !n.leida).length;

    // MODIFICADO: Función que se ejecuta al hacer clic en "Cerrar sesión"
    const handleLogout = useCallback(() => {

        // Limpiar intervalo si existe
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        // Limpiar contexto de audio
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }

        // NUEVO: Limpiar datos de notificaciones al cerrar sesión
        localStorage.removeItem(STORAGE_KEYS.PETICIONES_NOTIFICADAS);
        localStorage.removeItem(STORAGE_KEYS.NOTIFICACIONES_PERSISTENTES);
        localStorage.removeItem(STORAGE_KEYS.ULTIMA_VERIFICACION);

        // Limpiar datos del localStorage
        localStorage.removeItem('authToken');
        localStorage.removeItem('idUsuario');
        localStorage.removeItem('nombreUsuario');

        // NUEVO: Mantener preferencia de modo oscuro al cerrar sesión
        // localStorage.removeItem(STORAGE_KEYS.DARK_MODE); // Comentado para mantener preferencia

        // Redirigir al login
        navigate('/login');
    }, [navigate]);

    // Función para alternar sonido
    const toggleSonido = useCallback(() => {
        setSonidoHabilitado(prev => !prev);
    }, []);

    // NUEVO: Función para navegar a las diferentes secciones
   const handleNavigation = useCallback((path: string) => {
    navigate(path);
    // El menú se cerrará automáticamente al hacer clic en una opción
}, [navigate]);

    // NUEVO: Menú de navegación rápida para el modo contraído
    const quickNavMenuItems = [
        {
            label: 'Productos',
            icon: 'fas fa-cube',
            command: () => handleNavigation('/producto')
        },
        {
            label: 'Entradas',
            icon: 'fas fa-arrow-circle-up',
            command: () => handleNavigation('/entrada')
        },
        {
            label: 'Salidas',
            icon: 'fas fa-arrow-circle-down',
            command: () => handleNavigation('/salida')
        },
        {
            label: 'Mantenimiento',
            icon: 'fas fa-wrench',
            command: () => handleNavigation('/mantenimiento')
        },
        {
            label: 'Peticiones',
            icon: 'fas fa-clipboard-check',
            command: () => handleNavigation('/peticionesadmin')
        }
    ];

    // MODIFICADO: Menú desplegable de usuario con opción de modo oscuro
    const userMenuItems = [
        {
            label: 'Configuración',
            icon: 'pi pi-cog',
            items: [
                {
                    label: modoOscuro ? 'Modo claro' : 'Modo oscuro',
                    icon: modoOscuro ? 'pi pi-sun' : 'pi pi-moon',
                    command: toggleModoOscuro,
                },
                {
                    label: sonidoHabilitado ? 'Silenciar notificaciones' : 'Activar sonido',
                    icon: sonidoHabilitado ? 'pi pi-volume-off' : 'pi pi-volume-up',
                    command: toggleSonido,
                },
            ],
        },
        {
            label: 'Cuenta',
            icon: 'pi pi-user',
            items: [
                {
                    label: 'Cerrar sesión',
                    icon: 'pi pi-sign-out',
                    command: handleLogout,
                },
            ],
        },
    ];

    // Componente de notificaciones personalizado
    const NotificationsContent = () => (
        <div style={{ width: '320px', maxHeight: '400px' }}>
            <div style={{ 
                padding: '1rem',
                borderBottom: '1px solid #e9ecef',
                backgroundColor: '#f8f9fa',
                fontWeight: 'bold',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <span>Peticiones Pendientes</span>
                <Badge value={notificacionesNoLeidas} severity="danger" />
            </div>
            
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {notificaciones.length > 0 ? (
                    notificaciones.slice(0, 5).map(notificacion => (
                        <div 
                            key={notificacion.id}
                            style={{ 
                                padding: '12px 16px',
                                borderBottom: '1px solid #eee',
                                cursor: 'pointer',
                                backgroundColor: notificacion.leida ? 'transparent' : '#f8f9fa',
                                transition: 'background-color 0.2s'
                            }}
                            onClick={() => handleNotificacionClick(notificacion)}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#e9ecef';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = notificacion.leida ? 'transparent' : '#f8f9fa';
                            }}
                        >
                            <div style={{ 
                                fontWeight: notificacion.leida ? 'normal' : 'bold',
                                fontSize: '0.9rem',
                                marginBottom: '4px',
                                color: '#333'
                            }}>
                                Petición #{notificacion.folio}
                            </div>
                            <div style={{ 
                                fontSize: '0.8rem',
                                color: '#666',
                                marginBottom: '6px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>
                                {notificacion.titulo}
                            </div>
                            <div style={{ 
                                fontSize: '0.75rem',
                                color: '#999',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '4px'
                            }}>
                                <span>{notificacion.empleado}</span>
                                <span style={{ 
                                    backgroundColor: getEstatusColor(notificacion.estatus),
                                    color: 'white',
                                    padding: '2px 6px',
                                    borderRadius: '10px',
                                    fontSize: '0.7rem'
                                }}>
                                    {notificacion.estatus}
                                </span>
                            </div>
                            <div style={{ 
                                fontSize: '0.7rem',
                                color: '#aaa'
                            }}>
                                {formatearFecha(notificacion.fecha)}
                            </div>
                        </div>
                    ))
                ) : (
                    <div style={{ 
                        padding: '2rem',
                        textAlign: 'center',
                        color: '#999',
                        fontStyle: 'italic'
                    }}>
                        No hay peticiones pendientes
                    </div>
                )}
            </div>
            
            {notificaciones.length > 0 && (
                <div style={{ 
                    padding: '12px 16px',
                    borderTop: '1px solid #e9ecef',
                    backgroundColor: '#f8f9fa',
                    display: 'flex',
                    gap: '8px'
                }}>
                    <Button
                        label="Ver todas"
                        icon="pi pi-external-link"
                        size="small"
                        outlined
                        onClick={irAPeticiones}
                        style={{ flex: 1 }}
                    />
                    <Button
                        label="Marcar leídas"
                        icon="pi pi-check"
                        size="small"
                        severity="secondary"
                        outlined
                        onClick={marcarTodasComoLeidas}
                        style={{ flex: 1 }}
                    />
                </div>
            )}
        </div>
    );

    return (
        <>
            <nav className="main-header navbar navbar-expand navbar-white navbar-light">
                {/* Left navbar links */}
                <ul className="navbar-nav">
                    <li className="nav-item d-none d-sm-inline-block">
                        <a
                            href="#"
                            className="nav-link"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/inicio');
                            }}
                        >
                            <i className="fas fa-home" style={{ marginRight: '5px' }}></i>
                            Inicio
                        </a>
                    </li>
                    
                    {/* MODIFICADO: Navegación rápida con opción de contraer */}
                    {menuNavegacionExpandido ? (
                        // MODO EXPANDIDO: Mostrar todos los enlaces
                        <>
                            <li className="nav-item d-none d-sm-inline-block">
                                <a
                                    href="#"
                                    className="nav-link"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleNavigation('/producto');
                                    }}
                                    
                                >
                                    <i className="fas fa-cube" style={{ marginRight: '5px' }}></i>
                                    Productos
                                </a>
                            </li>
                            
                            <li className="nav-item d-none d-sm-inline-block">
                                <a
                                    href="#"
                                    className="nav-link"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleNavigation('/entrada');
                                    }}
                               
                                >
                                    <i className="fas fa-arrow-circle-up" style={{ marginRight: '5px' }}></i>
                                    Entradas
                                </a>
                            </li>
                            
                            <li className="nav-item d-none d-sm-inline-block">
                                <a
                                    href="#"
                                    className="nav-link"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleNavigation('/salida');
                                    }}
                                   
                                >
                                    <i className="fas fa-arrow-circle-down" style={{ marginRight: '5px' }}></i>
                                    Salidas
                                </a>
                            </li>
                            
                            <li className="nav-item d-none d-sm-inline-block">
                                <a
                                    href="#"
                                    className="nav-link"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleNavigation('/mantenimiento');
                                    }}
                                  
                                >
                                    <i className="fas fa-wrench" style={{ marginRight: '5px' }}></i>
                                    Mantenimiento
                                </a>
                            </li>
                            
                            <li className="nav-item d-none d-sm-inline-block">
                                <a
                                    href="#"
                                    className="nav-link"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleNavigation('/peticionesadmin');
                                    }}
                                   
                                >
                                    <i className="fas fa-clipboard-check" style={{ marginRight: '5px' }}></i>
                                    Peticiones
                                </a>
                            </li>
                        </>
                    ) : (
                        // MODO CONTRAÍDO: Mostrar solo el botón "Más"
                        <li className="nav-item d-none d-sm-inline-block">
                            <Menu model={quickNavMenuItems} popup ref={quickNavMenu} id="quick-nav-menu" />
                            <a
                                href="#"
                                className="nav-link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    quickNavMenu.current?.toggle(e);
                                }}
                                
                            >
                                <i className="fas fa-bars" style={{ marginRight: '5px' }}></i>
                                Más
                            </a>
                        </li>
                    )}
                    
                    {/* NUEVO: Botón para contraer/expandir el menú */}
                    <li className="nav-item d-none d-lg-inline-block">
                        <a
                            href="#"
                            className="nav-link"
                            onClick={(e) => {
                                e.preventDefault();
                                toggleMenuNavegacion();
                            }}
                            
                            style={{ 
                                color: '#6c757d',
                                fontSize: '0.9rem'
                            }}
                        >
                            <i className={menuNavegacionExpandido ? 'fas fa-chevron-left' : 'fas fa-chevron-right'}></i>
                        </a>
                    </li>
                </ul>

                {/* Right navbar links */}
                <ul className="navbar-nav ml-auto">
                    {/* NUEVO: Botón de búsqueda global */}
                    <li className="nav-item">
                        <button
                            className="nav-link p-link"
                            onClick={() => showSearch()}
                      
                            style={{ 
                                background: 'none',
                                border: 'none',
                                color: '#495057',
                                fontSize: '1.1rem',
                                padding: '0.5rem'
                            }}
                        >
                            <i className="fas fa-search" />
                        </button>
                    </li>

                    {/* NUEVO: Botón de modo oscuro */}
                    <li className="nav-item">
                        <button
                            className="nav-link p-link"
                            onClick={toggleModoOscuro}
                            title={modoOscuro ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
                            style={{ 
                                background: 'none',
                                border: 'none',
                                color: '#495057',
                                fontSize: '1.1rem',
                                padding: '0.5rem',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            <i className={modoOscuro ? 'fas fa-sun' : 'fas fa-moon'} />
                        </button>
                    </li>

                    {/* Botón de notificaciones */}
                    <li className="nav-item">
                        <OverlayPanel ref={notificationsPanel}>
                            <NotificationsContent />
                        </OverlayPanel>
                        <button
                            className={`nav-link p-link position-relative ${notificacionesNoLeidas > 0 ? 'notification-pulse' : ''}`}
                            onClick={(e) => notificationsPanel.current?.toggle(e)}
                            style={{ 
                                background: 'none',
                                border: 'none',
                                color: '#495057',
                                fontSize: '1.1rem',
                                padding: '0.5rem',
                                position: 'relative'
                            }}
                            
                        >
                            <i className="fas fa-bell" />
                            {notificacionesNoLeidas > 0 && (
                                <Badge 
                                    value={notificacionesNoLeidas > 99 ? '99+' : notificacionesNoLeidas.toString()} 
                                    severity="danger"
                                    style={{
                                        position: 'absolute',
                                        top: '0',
                                        right: '0',
                                        transform: 'translate(50%, -50%)',
                                        minWidth: '18px',
                                        height: '18px',
                                        fontSize: '0.7rem',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                />
                            )}
                        </button>
                    </li>

                    <li className="nav-item">
                        <Menu model={userMenuItems} popup ref={menu} id="user-menu" />
                        <button
                            className="nav-link p-link"
                            onClick={(e) => menu.current?.toggle(e)}
                            style={{ 
                                background: 'none',
                                border: 'none',
                                color: '#495057'
                            }}
                        >
                            <i className="pi pi-user" />
                        </button>
                    </li>
                </ul>

                {/* NUEVO: Estilos CSS SIMPLIFICADOS Y FUNCIONALES para modo oscuro */}
                <style>
                    {`
                        /* MODO OSCURO SIMPLE Y FUNCIONAL */
                        .dark-mode {
                            background-color: #1a1a1a !important;
                            color: #ffffff !important;
                        }

                        /* Aplicar modo oscuro a elementos específicos */
                        .dark-mode *:not(.p-badge):not(.badge):not([style*="background-color: #ffc107"]):not([style*="background-color: #28a745"]):not([style*="background-color: #dc3545"]):not([style*="background-color: #17a2b8"]):not([style*="background-color: #6c757d"]) {
                            background-color: #2d2d2d !important;
                            color: #ffffff !important;
                            border-color: #555555 !important;
                        }

                        /* Navbar en modo oscuro */
                        .dark-mode .navbar {
                            background-color: #2d2d2d !important;
                            border-bottom: 1px solid #555555 !important;
                        }

                        .dark-mode .navbar .nav-link {
                            color: #ffffff !important;
                        }

                        .dark-mode .navbar .nav-link:hover {
                            background-color: #404040 !important;
                            color: #ffffff !important;
                        }

                        /* Componentes PrimeReact en modo oscuro */
                        .dark-mode .p-menu {
                            background-color: #2d2d2d !important;
                            border: 1px solid #555555 !important;
                            color: #ffffff !important;
                        }

                        .dark-mode .p-menuitem-link {
                            color: #ffffff !important;
                        }

                        .dark-mode .p-menuitem-link:hover {
                            background-color: #404040 !important;
                        }

                        .dark-mode .p-overlaypanel {
                            background-color: #2d2d2d !important;
                            border: 1px solid #555555 !important;
                        }

                        .dark-mode .p-dialog {
                            background-color: #1a1a1a !important;
                        }

                        .dark-mode .p-dialog .p-dialog-content {
                            background-color: #1a1a1a !important;
                            color: #ffffff !important;
                        }

                        .dark-mode .p-inputtext {
                            background-color: #2d2d2d !important;
                            border-color: #555555 !important;
                            color: #ffffff !important;
                        }

                        .dark-mode .p-button {
                            background-color: #404040 !important;
                            border-color: #555555 !important;
                            color: #ffffff !important;
                        }

                        /* Elementos específicos que deben mantener colores */
                        .dark-mode .p-badge,
                        .dark-mode .badge {
                            filter: none !important;
                        }

                        /* NUEVO: Estilos para el botón scroll to top */
                        .scroll-to-top-button {
                            position: fixed;
                            bottom: 30px;
                            right: 30px;
                            width: 50px;
                            height: 50px;
                            border-radius: 50%;
                            background: linear-gradient(135deg, #007bff, #0056b3);
                            color: white;
                            border: none;
                            cursor: pointer;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: 1.2rem;
                            box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
                            transition: all 0.3s ease;
                            z-index: 1000;
                            opacity: 0;
                            transform: translateY(20px);
                        }

                        .scroll-to-top-button.visible {
                            opacity: 1;
                            transform: translateY(0);
                        }

                        .scroll-to-top-button:hover {
                            background: linear-gradient(135deg, #0056b3, #004085);
                            transform: translateY(-2px);
                            box-shadow: 0 6px 16px rgba(0, 123, 255, 0.4);
                        }

                        .scroll-to-top-button:active {
                            transform: translateY(0);
                        }

                        /* Modo oscuro para el botón scroll to top */
                        .dark-mode .scroll-to-top-button {
                            background: linear-gradient(135deg, #6c757d, #495057);
                            box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
                        }

                        .dark-mode .scroll-to-top-button:hover {
                            background: linear-gradient(135deg, #495057, #343a40);
                            box-shadow: 0 6px 16px rgba(108, 117, 125, 0.4);
                        }

                        /* Animación de entrada del botón */
                        @keyframes slideInUp {
                            from {
                                opacity: 0;
                                transform: translateY(20px);
                            }
                            to {
                                opacity: 1;
                                transform: translateY(0);
                            }
                        }

                        .scroll-to-top-button.visible {
                            animation: slideInUp 0.3s ease-out;
                        }

                        /* Responsive para el botón scroll to top */
                        @media (max-width: 768px) {
                            .scroll-to-top-button {
                                bottom: 20px;
                                right: 20px;
                                width: 45px;
                                height: 45px;
                                font-size: 1.1rem;
                            }
                        }

                        /* Transiciones suaves */
                        * {
                            transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease !important;
                        }

                        /* Estilos existentes para navegación */
                        .p-menu .p-menuitem-link {
                            padding: 0.5rem 1rem;
                        }
                        
                        .p-menu .p-menuitem-text {
                            line-height: 1.4;
                        }
                        
                        .nav-link.p-link:hover {
                            background-color: rgba(0, 0, 0, 0.05);
                            border-radius: 4px;
                        }
                        
                        .p-badge {
                            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                        }
                        
                        @keyframes pulse {
                            0% { transform: scale(1); }
                            50% { transform: scale(1.1); }
                            100% { transform: scale(1); }
                        }
                        
                        .notification-pulse {
                            animation: pulse 2s infinite;
                        }
                        
                        .notification-pulse:hover {
                            animation-play-state: paused;
                        }
                        
                        .p-overlaypanel .p-overlaypanel-content {
                            padding: 0;
                        }
                        
                        /* ESTILOS PARA NAVEGACIÓN RÁPIDA */
                        .navbar-nav .nav-link {
                            transition: all 0.3s ease;
                            border-radius: 4px;
                            margin: 0 2px;
                        }
                        
                        .navbar-nav .nav-link:hover {
                            background-color: rgba(0, 0, 0, 0.05);
                            transform: translateY(-1px);
                        }
                        
                        .dark-mode .navbar-nav .nav-link:hover {
                            background-color: #404040 !important;
                        }
                        
                        .navbar-nav .nav-link i {
                            transition: transform 0.2s ease;
                        }
                        
                        .navbar-nav .nav-link:hover i {
                            transform: scale(1.1);
                        }
                        
                        /* Estilos para el menú de navegación rápida */
                        #quick-nav-menu .p-menuitem-link {
                            display: flex;
                            align-items: center;
                            gap: 0.5rem;
                        }
                        
                        #quick-nav-menu .p-menuitem-icon {
                            width: 1.2rem;
                            text-align: center;
                        }

                        /* Estilo para el botón de modo oscuro */
                        .navbar-nav .nav-link[title*="modo"] i {
                            transition: all 0.3s ease;
                        }

                        .navbar-nav .nav-link[title*="modo"]:hover i {
                            transform: rotate(15deg) scale(1.1);
                        }
                        
                        /* Responsive: Ajustes para diferentes tamaños de pantalla */
                        @media (max-width: 1200px) {
                            .navbar-nav .nav-link {
                                padding: 0.5rem 0.3rem;
                                font-size: 0.9rem;
                            }
                        }
                        
                        @media (max-width: 992px) {
                            .navbar-nav .nav-link {
                                padding: 0.5rem 0.25rem;
                                font-size: 0.8rem;
                            }
                        }
                        
                        /* Animación para el botón "Más" */
                        .navbar-nav .nav-link[title="Navegación rápida"] i {
                            animation: fadeIn 0.3s ease-in-out;
                        }
                        
                        @keyframes fadeIn {
                            from { opacity: 0; transform: scale(0.8); }
                            to { opacity: 1; transform: scale(1); }
                        }
                    `}
                </style>
            </nav>

            {/* NUEVO: Botón flotante scroll to top */}
            <button
                className={`scroll-to-top-button ${mostrarScrollToTop ? 'visible' : ''}`}
                onClick={scrollToTop}
                title="Volver al inicio"
                aria-label="Volver al inicio de la página"
            >
                <i className="fas fa-chevron-up" />
            </button>

            {/* NUEVO: Componente de búsqueda global */}
            <GlobalSearch
                isVisible={isSearchVisible}
                onHide={hideSearch}
                initialQuery={searchQuery}
            />
        </>
    );
};

export default Navbar;